import L from 'leaflet'
import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { LayerGroup, LayersControl } from 'react-leaflet'
import { ILocality, IMooring, IMooringType, IPosition, IPositionType } from '../../../../../generated-types'
import { CreateMooring } from './CreateMooring'

interface IDrawnGeometryLayersProps {
  positions?: IPosition[]
  moorings?: IMooring[]
  localities?: ILocality[]
  mapPosition: L.LatLngExpression
  mooringType?: IMooringType
  isModalView: boolean
  checked?: boolean
  onPositionSelect?: (positionId: number, positionName: string) => void
}

export const DrawnGeometryLayers: React.FC<IDrawnGeometryLayersProps> = memo(
  ({ positions, moorings, localities, mapPosition, mooringType, isModalView, checked = true, onPositionSelect }) => {
    const { t } = useTranslation(['mooring-layout', 'map'])

    const createLayerGroup = useCallback(
      (type: IPositionType) => {
        if (positions) {
          return (
            <LayerGroup>
              <CreateMooring
                mooring={{ positions, type: mooringType } as IMooring}
                type={type}
                mapPosition={mapPosition}
                isModalView={isModalView}
                onPositionSelect={onPositionSelect}
              />
            </LayerGroup>
          )
        }

        const allMoorings = moorings ?? localities?.flatMap(locality => locality.moorings ?? []) ?? []
        return (
          <LayerGroup>
            {allMoorings.map((mooring, index) => (
              <CreateMooring
                key={index}
                mooring={mooring}
                type={type}
                mapPosition={mapPosition}
                isFleet={mooring.type === IMooringType.Barge}
                isModalView={isModalView}
                onPositionSelect={onPositionSelect}
              />
            ))}
          </LayerGroup>
        )
      },
      [positions, moorings, localities, mapPosition, mooringType, isModalView, onPositionSelect],
    )

    return (
      <>
        <LayersControl.Overlay name={t('power-lines', { ns: 'map' }) as string} checked={checked}>
          {createLayerGroup(IPositionType.ElectricityLine)}
        </LayersControl.Overlay>
        <LayersControl.Overlay name={t('recapture-zones', { ns: 'map' }) as string} checked={checked}>
          {createLayerGroup(IPositionType.Polygon)}
        </LayersControl.Overlay>
      </>
    )
  },
)
